import axios from "axios";
import { memberToken } from "./memberToken";
import { ENV, coreState, LOCAL_PROFILE } from "./environment";
import { isTestAddress } from "./memberDerive";

// This app has no local backend: the shared Core origin is validated per environment.
export const API = `${ENV.core}/api`;

// Local throw-away stack: fetch transport that never follows a redirect and never sends cookies or uses the HTTP cache.
if (LOCAL_PROFILE) Object.assign(axios.defaults, { adapter: "fetch", withCredentials: false, fetchOptions: { redirect: "error", cache: "no-store" } });

// Transport header for the JWT.
// - Preview default: "x-member-token" (Emergent edge allows custom headers via ACAH:*).
// - Production hardening: set REACT_APP_MEMBER_AUTH_HEADER=authorization to use
//   `Authorization: Bearer <jwt>` once a dedicated API domain is in place.
const AUTH_HEADER = (process.env.REACT_APP_MEMBER_AUTH_HEADER || "x-member-token").toLowerCase();

function applyAuthHeader(config, token) {
  if (!token) return config;
  if (AUTH_HEADER === "authorization") {
    config.headers["Authorization"] = `Bearer ${token}`;
  } else {
    config.headers["X-Member-Token"] = token;
  }
  return config;
}

// No cookies for the separated preview auth (credentials omitted).
export const api = axios.create({ baseURL: API, withCredentials: false });

// Only member endpoints of a verified Core are reachable; staff/admin APIs never are.
function guardError(code, config) {
  return Object.assign(new Error(code), { memberGuard: code, config });
}

api.interceptors.request.use((config) => {
  if (ENV.problems.length || coreState.problem) throw guardError("env_blocked", config);
  if (!/^\/member\//.test(config.url || "")) throw guardError("not_member_api", config);
  // Local stack: only fictitious test addresses ever reach the login.
  if (LOCAL_PROFILE && /^\/member\/auth\/login$/.test(config.url) && !isTestAddress(config.data?.email)) throw guardError("local_account", config);
  config._memberSessionVersion = memberToken.getSessionVersion();
  return applyAuthHeader(config, memberToken.getAccess());
});

let pendingRefresh = null;

// Only an explicit JSON 401/403 ends the session; network errors, timeouts, 5xx or ingress pages never do.
export function refreshFailure(err) {
  const res = err?.response;
  const json = (res?.headers?.["content-type"] || "").toLowerCase().includes("application/json") || res?.data?.detail != null;
  return (res?.status === 401 || res?.status === 403) && json ? "expired" : "transient";
}

// Session state unknown (refresh unreachable): callers show a connection problem, tokens stay.
const refreshUnavailable = (config) => Object.assign(new Error("refresh_unavailable"), { memberRefreshTransient: true, config });

// On 401, try a single silent refresh (in-memory refresh token), then retry once.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const version = memberToken.getSessionVersion();
    const refreshToken = memberToken.getRefresh();
    const authAction = /\/member\/auth\/(login|logout|refresh)(?:[?#]|$)/.test(original?.url || "");
    const current = status === 401 && original && !authAction && original._memberSessionVersion === version;
    // A freshly renewed token rejected again means the session was revoked.
    if (current && original._retried && refreshToken && refreshFailure(error) === "expired") {
      memberToken.end("expired");
      return Promise.reject(error);
    }
    if (current && !original._retried && refreshToken) {
      original._retried = true;
      if (!pendingRefresh || pendingRefresh.version !== version) {
        pendingRefresh = {
          version,
          promise: axios.post(
            `${API}/member/auth/refresh`,
            { refresh_token: refreshToken },
            { withCredentials: false },
          ),
        };
      }
      const refresh = pendingRefresh;
      try {
        const { data } = await refresh.promise;
        // Logout or another login invalidates both the result and the retry.
        if (memberToken.getSessionVersion() !== version) return Promise.reject(error);
        if (typeof data?.access_token !== "string" || !data.access_token) return Promise.reject(refreshUnavailable(original));
        memberToken.set(data.access_token);
        return api(applyAuthHeader(original, data.access_token));
      } catch (e) {
        if (memberToken.getSessionVersion() !== version) return Promise.reject(error);
        if (refreshFailure(e) === "transient") return Promise.reject(refreshUnavailable(original));
        memberToken.end("expired");
        return Promise.reject(error);
      } finally {
        // A late old result must not discard a newer session's refresh.
        if (pendingRefresh === refresh) pendingRefresh = null;
      }
    }
    return Promise.reject(error);
  },
);

// True when the failure is a connectivity/backend-availability problem
// (network error, timeout, 5xx, or an ingress 404 while the shared core sleeps)
// rather than a normal validation/auth error with a member-readable detail.
export function isConnectionError(err) {
  if (!err || err.memberGuard || err.memberSessionProblem) return false;
  const status = err.response?.status;
  if (status == null) return true;        // network error / timeout / CORS
  if (status >= 500) return true;         // 502/503/504
  if (status === 404) {
    // Sleeping shared core: the ingress returns a plain-text 404 (no JSON detail).
    // A genuine API 404 (JSON with a detail) is NOT treated as a connection issue.
    const ct = (err.response?.headers?.["content-type"] || "").toLowerCase();
    if (!err.response?.data?.detail && !ct.includes("application/json")) return true;
  }
  return false;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
